import { FC, useState, ReactNode } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { trackEvent } from 'dibs-tracking';
import { PdpSharedStorytellingTrackingWrapper } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';

import { PdpRespStorytellingModule_item } from './__generated__/PdpRespStorytellingModule_item.graphql';
import { PdpRespStorytellingTab } from './PdpRespStorytellingTab';

import styles from './PdpRespStorytellingModule.scss';

type Props = {
    item: PdpRespStorytellingModule_item;
};

const PRIMARY_TAB = 'primaryTab';
const SECONDARY_TAB = 'secondaryTab';

function fireTabTracking(): void {
    trackEvent({
        category: 'product',
        action: 'storytelling module tab clicked',
        label: 'pdp',
    });
}

const PdpRespStorytellingModuleComponent: FC<Props> = ({ item }) => {
    const [activeTab, setActiveTab] = useState(PRIMARY_TAB);
    const tabs = item?.storyTellingModule?.tabs || [];
    const hasTwoTabs = tabs.length === 2;
    if (!tabs.length) {
        return null;
    } else if (hasTwoTabs) {
        return (
            <PdpSharedStorytellingTrackingWrapper>
                <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                    <TabList indicatorPadding="xSmall">
                        {tabs.map((tab, index): [string, ReactNode] => [
                            index === 0 ? PRIMARY_TAB : SECONDARY_TAB,
                            tab && (
                                <span
                                    key={`storytelling-tab-${tab.tabTitle}`}
                                    className={styles.tabLabel}
                                    role="button"
                                    tabIndex={0}
                                    onClick={fireTabTracking}
                                    onKeyDown={e => {
                                        // enter or space should fire click event
                                        switch (e.key) {
                                            case ' ':
                                            case 'Spacebar':
                                            case 'Enter':
                                                fireTabTracking();
                                                break;
                                            default:
                                                break;
                                        }
                                    }}
                                >
                                    {tab.tabTitle}
                                </span>
                            ),
                        ])}
                    </TabList>
                    <TabPanels>
                        {tabs.map((tab, index): [string, ReactNode] => [
                            index === 0 ? PRIMARY_TAB : SECONDARY_TAB,
                            tab && (
                                <PdpRespStorytellingTab
                                    key={`storytelling-tab-${tab.tabTitle}-tab`}
                                    storyTellingTab={tab}
                                />
                            ),
                        ])}
                    </TabPanels>
                </TabProvider>
            </PdpSharedStorytellingTrackingWrapper>
        );
    } else {
        return (
            tabs[0] && (
                <PdpSharedStorytellingTrackingWrapper>
                    <PdpRespStorytellingTab storyTellingTab={tabs[0]} />
                </PdpSharedStorytellingTrackingWrapper>
            )
        );
    }
};

export const PdpRespStorytellingModule = createFragmentContainer(
    PdpRespStorytellingModuleComponent,
    {
        item: graphql`
            fragment PdpRespStorytellingModule_item on Item {
                storyTellingModule {
                    tabs {
                        tabTitle
                        ...PdpRespStorytellingTab_storyTellingTab
                    }
                }
            }
        `,
    }
);
